import { useEffect, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { supabase } from '@/lib/supabase';
import {
  DIAGNOSE_TEXT_MAX,
  diagnoseTextForReferral,
} from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type SelectedDiagnosis = { code: string; textDa: string };

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function DiagnosisField({
  inputId,
  inputRef,
  label,
  selected,
  onSelect,
  onClear,
}: {
  inputId: string;
  inputRef?: Ref<HTMLInputElement>;
  label: string;
  selected: SelectedDiagnosis | null;
  onSelect: (diagnosis: SelectedDiagnosis) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(selected ? '' : query, 300);
  const search = useQuery({
    queryKey: ['sks-diagnoses', debounced],
    enabled: !selected && debounced.length >= 2,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_sks_diagnoses', {
        p_query: debounced,
        p_limit: 20,
      });
      if (error) throw error;
      return data ?? [];
    },
  });

  if (selected) {
    const sent = diagnoseTextForReferral(selected.textDa);
    return (
      <div className="rounded-md border border-ds-line p-3">
        <p className={cn(TYPE.body, 'text-ds-text ')}>
          {selected.code} — {selected.textDa}
        </p>
        {selected.textDa.length > DIAGNOSE_TEXT_MAX && (
          <p className={cn('mt-1', FORM_HINT_TYPE)}>
            {t(
              'patients.hospital_referral.diagnosis_truncated',
              'Diagnoseteksten i henvisningen er de første {{max}} tegn: {{text}}',
              { max: DIAGNOSE_TEXT_MAX, text: sent },
            )}
          </p>
        )}
        <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={onClear}>
          {t('patients.hospital_referral.remove', 'Fjern')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Input
        id={inputId}
        ref={inputRef}
        label={label}
        value={query}
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
      />
      {search.isFetching && (
        <p className={cn('mt-2', FORM_HINT_TYPE)}>{t('patients.hospital_referral.searching', 'Søger…')}</p>
      )}
      {search.isError && (
        <p className={cn('mt-2', FORM_ERROR_TYPE)}>
          {t('patients.hospital_referral.search_failed', 'Søgningen fejlede.')}
        </p>
      )}
      {search.data && search.data.length === 0 && (
        <p className={cn('mt-2', FORM_HINT_TYPE)}>
          {t('patients.hospital_referral.no_results', 'Ingen resultater')}
        </p>
      )}
      {search.data && search.data.length > 0 && (
        <ul className="mt-2 max-h-60 space-y-1 overflow-auto">
          {search.data.map((row) => (
            <li key={row.code}>
              <button
                type="button"
                className="w-full rounded-md border border-ds-line px-3 py-2 text-left hover:bg-ds-row-hover"
                onClick={() => onSelect({ code: row.code, textDa: row.text_da })}
              >
                <span className={cn(TYPE.body, 'text-ds-text ')}>
                  {row.code} — {row.text_da}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
